import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, ArrowUpRight, MapPin, Clock, Mail, Loader2, Check } from "lucide-react";
import { Overline, SectionHeader } from "@/components/Section";
import HeroArtisanImage from "@/components/HeroArtisanImage";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
    "Ouverture de porte (claquée / bloquée)",
    "Dépannage / réparation",
    "Remplacement de serrure",
    "Pose de serrure A2P",
    "Pose de porte blindée",
    "Autre / je ne sais pas",
];

const Contact = () => {
    const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", service: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
            toast.error("Merci d'indiquer votre nom, téléphone et email.");
            return;
        }
        // Email basique
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
        if (!emailOk) {
            toast.error("Merci d'indiquer une adresse email valide.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            setDone(true);
            toast.success("Demande envoyée. Nous vous rappelons rapidement.");
            setForm({ name: "", phone: "", email: "", city: "", service: "", message: "" });
        } catch (err) {
            toast.error("Une erreur est survenue. Appelez-nous directement au 06 49 50 18 43.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <section className="relative border-b border-black/10 grid-bg" data-testid="contact-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-16 lg:pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-7 fade-up fade-up-1">
                            <Overline className="mb-5">◉ Contact</Overline>
                            <h1 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                                Devis gratuit<br /><span className="text-[#E63946]">en 2 minutes.</span>
                            </h1>
                            <p className="mt-7 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-2xl">
                                Décrivez votre besoin et l'artisan vous rappelle rapidement avec un tarif clair.
                                Pour toute urgence — porte claquée, effraction, serrure bloquée — privilégiez l'appel direct.
                            </p>
                        </div>
                        <div className="lg:col-span-5 fade-up fade-up-2">
                            <HeroArtisanImage />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-24 bg-white border-b border-black/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        {/* COORDONNEES */}
                        <div className="lg:col-span-5 space-y-5">
                            <a href="tel:+33649501843" data-testid="contact-call-card" className="block bg-[#E63946] hover:bg-[#D62828] text-white p-7 sm:p-9 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="text-xs uppercase tracking-[0.2em] font-semibold">Urgence 24/7</div>
                                    <Phone className="h-5 w-5" strokeWidth={2.4} />
                                </div>
                                <div className="mt-6 font-display font-bold uppercase text-3xl sm:text-4xl tracking-tight">06 49 50 18 43</div>
                                <div className="mt-3 text-sm text-white/90">Appel direct, réponse immédiate.</div>
                                <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold">
                                    Appeler maintenant <ArrowUpRight className="h-4 w-4" />
                                </div>
                            </a>

                            <div className="bg-white border border-black/10 p-7 sm:p-9 space-y-5">
                                <div className="flex items-start gap-4">
                                    <div className="h-10 w-10 grid place-items-center bg-[#0A0A0A] text-white flex-shrink-0">
                                        <Clock className="h-4 w-4" strokeWidth={2.4} />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">Disponibilité</div>
                                        <div className="font-display font-bold mt-1">24h/24 — 7j/7</div>
                                        <div className="text-sm text-zinc-600 mt-1">Jours fériés inclus</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="h-10 w-10 grid place-items-center bg-[#0A0A0A] text-white flex-shrink-0">
                                        <MapPin className="h-4 w-4" strokeWidth={2.4} />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">Zone d'intervention</div>
                                        <div className="font-display font-bold mt-1">Dépannage Express</div>
                                        <div className="text-sm text-zinc-600 mt-1">Intervention dans la commune — déplacement rapide jour et nuit.</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="h-10 w-10 grid place-items-center bg-[#0A0A0A] text-white flex-shrink-0">
                                        <Mail className="h-4 w-4" strokeWidth={2.4} />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">Devis & demandes</div>
                                        <div className="font-display font-bold mt-1">Formulaire en ligne</div>
                                        <div className="text-sm text-zinc-600 mt-1">Réponse rapide, devis sans engagement.</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* FORM */}
                        <div className="lg:col-span-7">
                            {done ? (
                                <div className="bg-white border border-black/10 p-8 sm:p-12" data-testid="contact-success">
                                    <div className="h-12 w-12 grid place-items-center bg-[#0A0A0A] text-white">
                                        <Check className="h-5 w-5" strokeWidth={2.6} />
                                    </div>
                                    <h3 className="mt-6 font-display font-bold text-2xl uppercase tracking-tight">Demande envoyée</h3>
                                    <p className="mt-3 text-zinc-600 leading-relaxed">
                                        Merci ! Nous vous rappelons dans les plus brefs délais avec un tarif clair.
                                        Pour toute urgence, appelez directement le <a href="tel:+33649501843" className="font-semibold text-[#0A0A0A] underline">06 49 50 18 43</a>.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setDone(false)}
                                        className="mt-7 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] hover:text-[#E63946] transition-colors"
                                        data-testid="contact-new-request"
                                    >
                                        Nouvelle demande <ArrowUpRight className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="bg-white border border-black/10 p-7 sm:p-10 space-y-6" data-testid="contact-form">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div className="space-y-2">
                                            <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] font-semibold">Nom *</Label>
                                            <Input
                                                id="name"
                                                value={form.name}
                                                onChange={(e) => update("name", e.target.value)}
                                                placeholder="Votre nom"
                                                className="rounded-none border-black/20 focus-visible:ring-0 focus-visible:border-black h-11"
                                                data-testid="contact-name-input"
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] font-semibold">Téléphone *</Label>
                                            <Input
                                                id="phone"
                                                type="tel"
                                                value={form.phone}
                                                onChange={(e) => update("phone", e.target.value)}
                                                placeholder="06 XX XX XX XX"
                                                className="rounded-none border-black/20 focus-visible:ring-0 focus-visible:border-black h-11"
                                                data-testid="contact-phone-input"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] font-semibold">Email *</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => update("email", e.target.value)}
                                            placeholder="vous@exemple.fr"
                                            className="rounded-none border-black/20 focus-visible:ring-0 focus-visible:border-black h-11"
                                            data-testid="contact-email-input"
                                            required
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div className="space-y-2">
                                            <Label htmlFor="city" className="text-xs uppercase tracking-[0.18em] font-semibold">Ville</Label>
                                            <Input
                                                id="city"
                                                value={form.city}
                                                onChange={(e) => update("city", e.target.value)}
                                                placeholder="Votre ville"
                                                className="rounded-none border-black/20 focus-visible:ring-0 focus-visible:border-black h-11"
                                                data-testid="contact-city-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs uppercase tracking-[0.18em] font-semibold">Service</Label>
                                            <Select value={form.service} onValueChange={(v) => update("service", v)}>
                                                <SelectTrigger
                                                    className="rounded-none border-black/20 focus:ring-0 focus:border-black h-11"
                                                    data-testid="contact-service-select"
                                                >
                                                    <SelectValue placeholder="Choisir un service" />
                                                </SelectTrigger>
                                                <SelectContent className="rounded-none">
                                                    {SERVICES.map((s) => (
                                                        <SelectItem key={s} value={s} className="rounded-none">{s}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] font-semibold">Décrivez votre besoin</Label>
                                        <Textarea
                                            id="message"
                                            value={form.message}
                                            onChange={(e) => update("message", e.target.value)}
                                            placeholder="Ex : porte claquée, j'ai oublié les clés à l'intérieur…"
                                            rows={5}
                                            className="rounded-none border-black/20 focus-visible:ring-0 focus-visible:border-black"
                                            data-testid="contact-message-input"
                                        />
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            data-testid="contact-submit-btn"
                                            className="inline-flex items-center justify-center gap-3 bg-[#0A0A0A] hover:bg-[#E63946] disabled:opacity-60 text-white px-7 py-4 transition-colors font-semibold text-sm uppercase tracking-[0.15em]"
                                        >
                                            {submitting ? (
                                                <><Loader2 className="h-4 w-4 animate-spin" /> Envoi…</>
                                            ) : (
                                                <>Envoyer ma demande <ArrowUpRight className="h-4 w-4" /></>
                                            )}
                                        </button>
                                        <a
                                            href="tel:+33649501843"
                                            className="inline-flex items-center justify-center gap-3 border border-black/15 hover:border-black px-7 py-4 transition-colors font-semibold text-sm uppercase tracking-[0.15em]"
                                            data-testid="contact-form-call-btn"
                                        >
                                            <Phone className="h-4 w-4" /> Ou appeler directement
                                        </a>
                                    </div>
                                    <p className="text-xs text-zinc-500">
                                        En envoyant ce formulaire, vous acceptez d'être recontacté par Atelier Beauchamp pour traiter votre demande.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Contact;
